// The tenant's ACTIVE scout-target registry — one select, one key, one shape.
//
// Why this exists (scouting flow audit 2026-09-14, F122 + F020/F051/F113).
// The same read was written out FIVE times: ScoutFormModal, QuickLogModal,
// ScoutingTrends, and the offline prewarm task — and the copies had drifted.
// The prewarm's `.select()` omitted `action_threshold` and `threshold_unit`,
// so a scout who tapped "Prepare for offline" (the documented morning
// workflow) walked the block with the action-threshold cue and the reading
// unit SILENTLY ABSENT from the capture form, while the same observation
// typed at the office showed both. A copy of a select string cannot be kept
// in step by review; a shared constant is kept in step by the compiler.
//
// ⚠️ If you add a column here, every consumer gets it — including the offline
// cache, which is the point. Keep the list minimal for that reason: this rides
// in the durable query cache on a field phone.
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'

/** The ONE column list. The prewarm task imports this too — do not fork it. */
export const SCOUT_TARGETS_ACTIVE_SELECT =
  'target_id, name, kind, default_severity, default_action, threshold_actions, action_threshold, threshold_unit'

// The key stays a LITERAL at both call sites, here and in the offline prewarm
// task. `check-prewarm-mirror` compares the two by reading the key out of the
// source, and a key built by a helper is one it cannot resolve — it would skip
// the task silently, which is precisely the drift it is there to catch.

// File-local: `ScoutTargets.tsx` declares its own same-named types for the
// EDITOR's shape (which carries is_active, deleted_at and the rest). These
// three describe what a CAPTURE surface reads, and nothing outside this file
// needs to name them — the hook's return type carries them.
type ScoutTargetKind = 'PEST' | 'DISEASE' | 'BLOOM' | 'GROWTH' | 'NUTRITIONAL' | 'OTHER'
type ScoutTargetSeverity = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL'

interface ScoutTarget {
  target_id:         string
  name:              string
  kind:              ScoutTargetKind
  default_severity:  ScoutTargetSeverity | null
  default_action:    string | null
  /** Mig 359 — per-severity action overrides, keyed by severity. */
  threshold_actions: Partial<Record<ScoutTargetSeverity, string>> | null
  /** Mig 974/975 — optional numeric action threshold + its unit. */
  action_threshold:  number | null
  threshold_unit:    string | null
}

/**
 * The active registry for one tenant.
 *
 * Returns the QUERY, not `data ?? []` — deliberately. Both capture forms told
 * the scout "No pest targets configured — add them under Scout Targets" off a
 * collapsed read (F129/F179, reproduced under a forced 500), which is a false
 * statement about the farm's configuration, names a page a FARM_SCOUT cannot
 * open, and pushes them into free text that then misses the registry name: no
 * default severity, no threshold cue, no breach alert, and a casing variant
 * that splits Trends. Callers must ask `readState(q)` before saying anything
 * about what the registry contains.
 */
export function useScoutTargetsActive(tenantId: string | null) {
  return useQuery<ScoutTarget[]>({
    queryKey: ['scout-targets-active', tenantId],
    enabled: !!tenantId,
    queryFn: async () => {
      // M87: scope to the active tenant explicitly — the read RLS ORs
      // is_platform_admin(), so an unscoped read merges EVERY tenant's
      // registry for a platform admin and the capture form then applies
      // another tenant's default severity / action / threshold.
      const { data, error } = await supabase.from('scout_targets')
        .select(SCOUT_TARGETS_ACTIVE_SELECT)
        .eq('tenant_id', tenantId!)
        .is('deleted_at', null)
        .eq('is_active', true)
        .order('name')
      if (error) throw error
      return (data ?? []) as unknown as ScoutTarget[]
    },
  })
}
